import { twMerge } from "tailwind-merge";
import {
  DonutChartBase,
  type DonutChartBaseProps,
  type DonutChartDataItem,
} from "@core/ui";
import { Type } from "../Type";

export type { DonutChartDataItem };

export type Props = Omit<
  DonutChartBaseProps,
  "renderSegment" | "renderLegendItem" | "renderCenter" | "renderEmptyCircle" | "renderFullCircle" | "renderBackgroundCircle"
>;

/**
 * Styled donut chart component wrapping DonutChartBase.
 * Displays data as a donut chart with optional legend and center content.
 */
export function DonutChart({
  data,
  size = 200,
  thickness = 20,
  className,
  children,
  showCenterLabel = false,
  showLegend = false,
  legendPosition = "bottom",
  showPercentage = false,
  isPercentageMeter = false,
  ...props
}: Props) {
  const getLegendPositionClass = () => {
    switch (legendPosition) {
      case "top":
        return "flex-col-reverse items-center";
      case "right":
        return "flex-row items-center";
      case "bottom":
        return "flex-col items-start";
      case "left":
        return "flex-row-reverse items-center";
      default:
        return "flex-col items-center";
    }
  };

  return (
    <DonutChartBase
      data={data}
      size={size}
      thickness={thickness}
      className={twMerge("flex gap-4", getLegendPositionClass(), className)}
      showCenterLabel={showCenterLabel}
      showLegend={showLegend}
      legendPosition={legendPosition}
      showPercentage={showPercentage}
      isPercentageMeter={isPercentageMeter}
      data-testid="donut-chart"
      renderEmptyCircle={({ cx, cy, r, strokeWidth }) => (
        <circle
          cx={cx}
          cy={cy}
          r={r}
          fill="none"
          className={twMerge("stroke-current", "text-brand-100")}
          strokeWidth={strokeWidth}
        />
      )}
      renderFullCircle={({ cx, cy, r, strokeWidth, color }) => (
        <circle
          cx={cx}
          cy={cy}
          r={r}
          fill="none"
          className={twMerge("stroke-current", `text-${color}`)}
          strokeWidth={strokeWidth}
        />
      )}
      renderBackgroundCircle={({ cx, cy, r, strokeWidth }) => (
        <circle
          cx={cx}
          cy={cy}
          r={r}
          fill="none"
          className="stroke-brand-100"
          strokeWidth={strokeWidth}
        />
      )}
      renderSegment={({ path, color, index }) => (
        <path
          key={index}
          d={path}
          fill="none"
          className={twMerge("stroke-current", `text-${color}`)}
          strokeWidth={thickness}
          strokeLinecap="round"
        />
      )}
      renderCenter={({ children: centerChildren }) => (
        <div className="absolute inset-0 flex items-center justify-center">
          {centerChildren}
        </div>
      )}
      renderLegendItem={({ item, index, percentage }) => (
        <div key={index} className="flex items-center gap-2">
          <div className={twMerge("h-3 w-3 rounded-full", `bg-${item.color}`)} />
          {item.label && (
            <Type variant="body2">
              {showPercentage && `${percentage}% `}
              {item.label}
            </Type>
          )}
        </div>
      )}
      {...props}
    >
      {children}
    </DonutChartBase>
  );
}

export default DonutChart;

